// Crime & incident ledger — a hash-chained, append-only record.
//
// Every entry carries the hash of the one before it, so a single altered or
// deleted row breaks the chain and `verifyChain()` says exactly where. Entries
// are local by default; nothing leaves the device unless the holder exports it.
//
// Rules baked in: no names unless the holder types them, no verdicts, no
// speculation about motive. It records what happened, when, and where.

import { getOption, setOption } from "./persistOptions";

const LEDGER = "safenet:ledger";

export type CrimeSource = "sighting" | "panic" | "escort" | "social" | "drone" | "family" | "manual";

export interface CrimeEntry {
  id: string;
  seq: number;
  at: number;
  kind: string;
  summary: string;
  lat?: number;
  lon?: number;
  audience?: string;
  source: CrimeSource;
  firstHand: boolean;
  /** Who logged it — the signed-in account when known, else "this device". */
  by: string;
  /** Free-text evidence pointers: photo names, page slugs, post URLs. */
  evidence: string[];
  prevHash: string;
  hash: string;
  reportedToAuthorities?: boolean;
  reference?: string;
}

export type CrimeInput = {
  kind: string;
  summary: string;
  lat?: number;
  lon?: number;
  audience?: string;
  source: CrimeSource;
  firstHand: boolean;
  by?: string;
  evidence?: string[];
};

export function ledger(): CrimeEntry[] { return getOption<CrimeEntry[]>(LEDGER, []); }
function writeLedger(l: CrimeEntry[]) { setOption(LEDGER, l); }

async function sha(s: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function body(e: Omit<CrimeEntry, "hash">): string {
  return JSON.stringify([e.seq, e.at, e.kind, e.summary, e.lat ?? "", e.lon ?? "", e.audience ?? "", e.source, e.firstHand, e.by, e.evidence, e.prevHash]);
}

/** Append an entry. Awaitable, but safe to fire and forget. */
export async function logCrime(input: CrimeInput): Promise<CrimeEntry> {
  const all = ledger();
  const prev = all[0];
  const draft: Omit<CrimeEntry, "hash"> = {
    id: crypto.randomUUID(),
    seq: (prev?.seq ?? 0) + 1,
    at: Date.now(),
    kind: input.kind,
    summary: input.summary.slice(0, 400),
    lat: input.lat,
    lon: input.lon,
    audience: input.audience,
    source: input.source,
    firstHand: input.firstHand,
    by: input.by ?? "this device",
    evidence: (input.evidence ?? []).slice(0, 20),
    prevHash: prev?.hash ?? "genesis",
  };
  const entry: CrimeEntry = { ...draft, hash: await sha(body(draft)) };
  writeLedger([entry, ...all].slice(0, 5000));
  return entry;
}

export function annotate(id: string, patch: { reportedToAuthorities?: boolean; reference?: string }): void {
  writeLedger(ledger().map((e) => (e.id === id ? { ...e, ...patch } : e)));
}

export interface ChainReport { ok: boolean; checked: number; brokenAt?: number; detail: string }

/** Re-walk the chain oldest→newest and re-hash every row. */
export async function verifyChain(): Promise<ChainReport> {
  const rows = [...ledger()].reverse();
  let prevHash = "genesis";
  for (const r of rows) {
    if (r.prevHash !== prevHash) {
      return { ok: false, checked: rows.length, brokenAt: r.seq, detail: `entry #${r.seq} does not follow the entry before it` };
    }
    const { hash, ...rest } = r;
    const again = await sha(body(rest));
    if (again !== hash) {
      return { ok: false, checked: rows.length, brokenAt: r.seq, detail: `entry #${r.seq} has been altered since it was written` };
    }
    prevHash = hash;
  }
  return { ok: true, checked: rows.length, detail: rows.length ? `all ${rows.length} entries verify against their seals` : "ledger is empty" };
}

export interface LedgerStats {
  total: number;
  last7: number;
  byKind: Array<{ kind: string; count: number }>;
  bySource: Array<{ source: string; count: number }>;
  reported: number;
}

export function ledgerStats(): LedgerStats {
  const all = ledger();
  const week = Date.now() - 7 * 86_400_000;
  const tally = (pick: (e: CrimeEntry) => string) => {
    const m = new Map<string, number>();
    for (const e of all) m.set(pick(e), (m.get(pick(e)) ?? 0) + 1);
    return [...m.entries()].map(([k, count]) => ({ k, count })).sort((a, b) => b.count - a.count);
  };
  return {
    total: all.length,
    last7: all.filter((e) => e.at >= week).length,
    byKind: tally((e) => e.kind).map(({ k, count }) => ({ kind: k, count })).slice(0, 12),
    bySource: tally((e) => e.source).map(({ k, count }) => ({ source: k, count })),
    reported: all.filter((e) => e.reportedToAuthorities).length,
  };
}

/** Court-friendly plain-text statement, in order, with seals shown. */
export function ledgerStatement(): string {
  const rows = [...ledger()].reverse();
  const lines = [
    "SKYE-NET.LIVE — INCIDENT LEDGER",
    `Exported ${new Date().toISOString()}`,
    `${rows.length} entries. Each entry carries the SHA-256 of the entry before it.`,
    "This is a personal record of what was observed. It contains no verdicts and no accusations.",
    "",
  ];
  for (const e of rows) {
    lines.push(`#${e.seq} · ${new Date(e.at).toISOString()} · ${e.kind} · via ${e.source}${e.firstHand ? " (first hand)" : " (second hand)"}`);
    lines.push(`  ${e.summary}`);
    if (e.lat !== undefined && e.lon !== undefined) lines.push(`  location: ${e.lat.toFixed(5)}, ${e.lon.toFixed(5)}`);
    if (e.audience) lines.push(`  reported as affecting: ${e.audience}`);
    if (e.evidence.length) lines.push(`  evidence: ${e.evidence.join(", ")}`);
    if (e.reportedToAuthorities) lines.push(`  reported to authorities${e.reference ? ` · ref ${e.reference}` : ""}`);
    lines.push(`  seal: ${e.hash}`);
    lines.push("");
  }
  return lines.join("\n");
}

export function downloadLedger(kind: "txt" | "json" = "txt"): void {
  const rows = [...ledger()].reverse();
  const text = kind === "json" ? JSON.stringify({ exportedAt: Date.now(), entries: rows }, null, 2) : ledgerStatement();
  const blob = new Blob([text], { type: kind === "json" ? "application/json" : "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `skyenet-incident-ledger.${kind}`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

/** Wipe the ledger. Deliberately loud: the holder must pass the exact phrase. */
export function wipeLedger(phrase: string): boolean {
  if (phrase.trim().toUpperCase() !== "ERASE THE LEDGER") return false;
  writeLedger([]);
  return true;
}
